// src/components/TableReveal.jsx
import React from 'react';
import { Box, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTableVisibility } from '../context/TableVisibilityContext';

/**
 * TableReveal
 * - Respects global "Show tables" toggle via TableVisibilityContext (showTables)
 * - When collapsed, CTA reads "Show table: All <Label>" and shows an eye icon
 * - When expanded, shows "Hide table" with an eye-off icon
 */
export default function TableReveal({
  sectionKey,                 // unique id per section/table (for test hooks)
  label = 'Table',            // e.g., "Triggers" → becomes "All Triggers" in collapsed CTA
  hideLabel = 'Hide table',
  initiallyOpen = false,      // local default when global is OFF
  children,
  sx
}) {
  const { showTables } = useTableVisibility();
  const [open, setOpen] = React.useState(!!initiallyOpen);

  // Keep local open state in sync with the global toggle
  React.useEffect(() => {
    setOpen(!!showTables);
  }, [showTables]);

  // Prefix "All " when hidden (unhide CTA label)
  const labelWithAll = React.useMemo(() => {
    const trimmed = String(label || '').trim();
    return trimmed.toLowerCase().startsWith('all ') ? trimmed : `All ${trimmed}`;
  }, [label]);

  if (!open) {
    return (
      <Box sx={{ mt: 1.5, ...sx }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => setOpen(true)}
          data-reveal={`show-${sectionKey}`}
          aria-label={`Show table: ${labelWithAll}`}
        >
          Show table: {labelWithAll}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5, ...sx }}>
      {children}
      <Box sx={{ mt: 1 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<VisibilityOffIcon />}
          onClick={() => setOpen(false)}
          data-reveal={`hide-${sectionKey}`}
          aria-label={hideLabel}
        >
          {hideLabel}
        </Button>
      </Box>
    </Box>
  );
}
